<template>
  <div :class="['framework-ratings',loading?'calculating-layout':'']">
    <a v-for="elementCode in selectedSchoolElementCodes"
      :key="elementCode"
      :href="'#'+elementCode"
      @click.prevent="navigateToTab(elementCode)"
      class="click-wrapper no-print-link"
      >
      <DivPrintableBackground :class="['rating-row', {'is-sa': elementCode==='SA'}]">
        <span class="name">{{ frElementNameForCode(elementCode) }}</span>
        <VizFourBarRating class="large"
          :rating="elementRatingForCode(elementCode)"
          :no-rating-text="noRatingTextForElementCode(elementCode)"
          :x-padding="3"
          :y-padding="0"
          :hollow-style="true"
          v-if="!isSelectedSchoolPK && !isCovidYear"
        />
        <VizFourBarRating class="small"
          :rating="elementRatingForCode(elementCode)"
          :no-rating-text="noRatingTextForElementCode(elementCode)"
          :x-padding="3"
          :y-padding="0"
          :hollow-style="true"
          v-if="!isSelectedSchoolPK && !isCovidYear"
        />
      </DivPrintableBackground>
    </a>
    <!-- added v-if to remove an empty component, which adds vertical space -->
    <FrameworkRatingFootnote v-if="displayHighOptOut" :footnote-type="FR_FOOTNOTE_TYPES.OPT_OUT"/>
    <FrameworkRatingFootnote v-if='displaySuppressSEorSA' :footnote-type="FR_FOOTNOTE_TYPES.SUPPRESS_SE_OR_SA"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Component, { mixins } from 'vue-class-component'

import { NS_CURRENT_REPORT } from './const'
import { frElementNameForCode } from '@/utils/data'

import { TabNavMixin } from '@/mixin'
import DivPrintableBackground from '@/components/DivPrintableBackground'
import VizFourBarRating from '@/components/VizFourBarRating'
import FrameworkRatingFootnote, { FR_FOOTNOTE_TYPES } from './FrameworkRatingFootnote'

@Component({
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'selectedSchoolType',
      'elementRatingForCode',
      'noRatingTextForCode',
      'selectedSchoolElementCodes',
      'isSelectedSchoolPK',
      'isCovidYear',
      'isSuppressSE',
      'isSuppressSA',
      'isHighOptOutSchool'
    ])
  },
  methods: {
    frElementNameForCode
  },
  components: {
    DivPrintableBackground,
    VizFourBarRating,
    FrameworkRatingFootnote
  }
})
export default class FrameworkRatings extends mixins(TabNavMixin) {
  loading = true // true on startup
  FR_FOOTNOTE_TYPES = FR_FOOTNOTE_TYPES

  /* Lifecycle */

  mounted () {
    this.loading = false
  }

  /* Methods - utility */
  noRatingTextForElementCode (c) {
    return this.noRatingTextForCode(c)
  }

  get displayHighOptOut () {
    return this.isHighOptOutSchool
  }

  get displaySuppressSEorSA () {
    return this.isSuppressSE || (!this.isHighOptOutSchool && this.isSuppressSA)
  }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/const.scss';
$name-margin-left-big: 18px;
$name-margin-left-small: 10px;
$rating-margin-right-big: 18px;
$rating-margin-right-small: 10px;

$sa-background: #D4D4D4;
.framework-ratings {
  width: 100%;
  font-size: 15px;
  border: 1px solid #777777;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
}
.click-wrapper {
  position: relative;
  display: block;
}
.rating-row {
  height: 34px;
  background-color: white;
  border-color: white;
  &.is-sa {
    background-color: $sa-background;
    border-color: $sa-background;
  }
  .name {
    float: left;
    margin-left: $name-margin-left-big;
    margin-top: 2px;
    color: black;
    font-weight: bold;
  }
  .viz-four-bar-rating {
    float: right;
    margin-right: $rating-margin-right-big;
    /deep/ .no-rating-text {
      color: black;
      font-size: 15px;
    }
    &.small /deep/ .no-rating-text {
      color: black;
      font-size: 11px;
    }
  }
}

.framework-rating-footnote.footnote {
  margin-left: $name-margin-left-big;
  margin-right: $rating-margin-right-big;
  margin-bottom: 6px;
}

/* Responsive rules */

.framework-ratings.calculating-layout {
  .viz-four-bar-rating.small {
    visibility: hidden;
  }
}
.framework-ratings:not(.calculating-layout) {
  .viz-four-bar-rating.small {
    display: none;
  }
}

/* upto 956px */
@media screen and (max-width: $screen-width-2-column-small) {
  .framework-ratings .rating-row {
    .name {
      margin-left: $name-margin-left-small;
    }
    .viz-four-bar-rating {
      margin-right: $rating-margin-right-small;
      &.small {
        display: inline-block;
      }
    }
  }
  .framework-ratings.calculating-layout {
    .viz-four-bar-rating.large {
      visibility: hidden;
    }
  }
  .framework-ratings:not(.calculating-layout) {
    .viz-four-bar-rating.large {
      display: none;
    }
  }
  .framework-rating-footnote.footnote {
    margin-left: $name-margin-left-small;
    margin-right: $rating-margin-right-small;
  }
}

/* similar code is in FrameworkRatingsLegend.vue, to avoid
duplicate code, we have to make a cCSS Containment Module
/* upto 644px */
@media screen and (max-width: $screen-width-1-column-large) {
.framework-ratings .rating-row {
  .name {
    margin-left: $name-margin-left-big;
  }
  .viz-four-bar-rating {
    margin-right: $rating-margin-right-big;
    &.large {
      display: inline-block;
    }
  }
}
.framework-ratings.calculating-layout {
  .viz-four-bar-rating.small {
    visibility: hidden;
  }
}
.framework-ratings:not(.calculating-layout) {
  .viz-four-bar-rating.small {
    display: none;
  }
}
  .framework-rating-footnote.footnote {
    margin-left: $name-margin-left-big;
    margin-right: $rating-margin-right-big;
  }
}

/* upto 456px */
@media screen and (max-width: $screen-width-1-column-small) {
.framework-ratings .rating-row {
  .name {
    margin-left: $name-margin-left-small;
  }
  .viz-four-bar-rating {
    margin-right: $rating-margin-right-small;
    &.small {
      display: inline-block;
    }
  }
}
.framework-ratings.calculating-layout {
  .viz-four-bar-rating.large {
    visibility: hidden;
  }
}
.framework-ratings:not(.calculating-layout) {
  .viz-four-bar-rating.large {
    display: none;
  }
}
  .framework-rating-footnote.footnote {
    margin-left: $name-margin-left-small;
    margin-right: $rating-margin-right-small;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/sqs/2023/FrameworkRatings.vue